import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { Badge,Button,Card,Divider,Stack,Switch,Tooltip,Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from '../../helpers/matomo';
import Checklists from "../../service/Checklists";
import queryClient from "../../service/query";
import { ChecklistType } from "../../types/checklist/types";
import DeletaFormulaio from "./Modal/DeletaFormulaio";
import DuplicaFormulario from "./Modal/DuplicaFormulario";

type props = {
  tipoServico: string,
  form: ChecklistType,
  disabled: boolean,
};

const formularioService = new Checklists();

export default function CardFormulario({ ...props }: props) {
  const { t } = useTranslation('translation');
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [openModalDuplica, setOpenModalDuplica] = useState<boolean>(false);

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_MODEL'],
    mutationFn: async () => {
      try {
        toast.info(`${t('checklists.savingChanges')}`);
        const token = await AttToken();
        if (!token) throw new Error('Token não encontrado');
        if (token) {
          const response = await formularioService.changeFormStatus(token, props.form.id, !props.form.status);
          if (!response) throw new Error('Erro ao atualizar status do modelo de formulário');
          return response;
        }
      } catch (error) {
        throw new Error('Erro ao atualizar status do modelo de formulário');
      }
    },
    onSuccess: () => {
      trackEventMatomo('gestão de formulários', 'sucesso', 'atualização', 'status do formulário atualizado');
      queryClient.refetchQueries({ queryKey: ['GET_CHECKLIST'] });
    },
    onError: () => trackEventMatomo('gestão de formulários', 'erro', 'atualização', 'falha ao atualizar status do formulário'),
  });

  function lowercaseExceptFirst(str: string) {
    if (typeof str !== 'string' || str.length === 0) {
      return '';
    }

    let formatted = str.charAt(0) + str.slice(1).toLowerCase();

    if (formatted.length > 100) {
      formatted = formatted.slice(0, 100) + '...';
    }

    return formatted;
  }

  function handleChangeSwitch(_event: React.ChangeEvent<HTMLInputElement>) {
    trackEventMatomo('gestão de formulários', 'click', 'switch', 'altera status do formulário');
    mutate();
  }

  function handleModalDelete() {
    trackEventMatomo('gestão de formulários', 'click', 'botão', 'abre confirmação de exclusão');
    setOpenModalDelete(!openModalDelete);
  }

  function handleModalDuplica() {
    trackEventMatomo('gestão de formulários', 'click', 'botão', 'abre duplicação de formulário');
    setOpenModalDuplica(!openModalDuplica);
  }


  const navigate = useNavigate();
  function routeChange() {
    trackEventMatomo('gestão de formulários', 'click', 'botão', 'edita formulário');
    const path = `/formularios/${props.form.id}`;
    navigate(path);
  };

  return (
    <>
      {openModalDelete
        && <DeletaFormulaio
          open={openModalDelete}
          handleCloseModal={handleModalDelete}
          id={props.form.id}
          nome={props.form.nome}
        />}
      {openModalDuplica && <DuplicaFormulario
        open={openModalDuplica}
        handleCloseModal={handleModalDuplica}
        id={props.form.id}
        nome={props.form.nome}
      />}
      <Card sx={{ width: '100%', height: '100%' }}>
        <Stack direction='row' spacing={1} alignItems='flex-start' justifyContent='space-between' padding={2}>
          <Stack width='100%' direction='column' spacing={2} alignItems='flex-start' justifyContent='flex-start'>
            <Tooltip title={props.form.nome} arrow>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#2EA689' }}>{lowercaseExceptFirst(props.form.nome)}</Typography>
            </Tooltip>
            <Divider variant="middle" orientation="horizontal" flexItem />
            <Typography fontSize='small' color='textDisabled' fontWeight={600}>{t('newForm.createBy')}&nbsp;
              <Typography component='span' fontSize='small' fontWeight={600} color='textPrimary'>
                {props.form.userNome}
              </Typography>
            </Typography>
            <Typography fontSize='small' color='textDisabled' fontWeight={600}>{t('newForm.version')}&nbsp;
              <Typography component='span' fontSize='small' fontWeight={600} color='textPrimary'>
                {props.form.versao}
              </Typography>
            </Typography>
            <Typography fontSize='small' color='textDisabled' fontWeight={600}>{t('newForm.serviceType')}&nbsp;
              <Typography component='span' fontSize='small' fontWeight={600} color='textPrimary'>
                {props.tipoServico}
              </Typography>
            </Typography>
            <Typography fontSize='small' color='textDisabled' fontWeight={600}>{t('newForm.fillingType')}&nbsp;
              <Typography component='span' fontSize='small' fontWeight={600} color='textPrimary'>
                {props.form.preenchimentoDeslogado ? t('newForm.anonimo') : t('newForm.logado')}
              </Typography>
            </Typography>
          </Stack>
          <Stack direction='column' spacing={1} alignItems='center' justifyContent='flex-start'>
            <Stack direction='column' spacing={1} alignItems='stretch' justifyContent='center'>
              <Button
                disabled={props.disabled || isPending}
                variant="outlined"
                size="large"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                endIcon={<BorderColorIcon />}
                onClick={routeChange}
              >
                {t('newForm.edit')}
              </Button>
              <Button
                disabled={props.disabled || isPending}
                variant="outlined"
                size="large"
                color="success"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                endIcon={<ContentCopyIcon />}
                onClick={() => handleModalDuplica()}
              >
                {t('newForm.duplicate')}
              </Button>
              <Button
                disabled={props.disabled || isPending}
                onClick={handleModalDelete}
                variant="outlined"
                color="error"
                size="large"
                sx={{ textTransform: 'none', fontWeight: 600 }}
                endIcon={<DeleteIcon />}
              >
                {t('newForm.delete')}
              </Button>
            </Stack>
            <Switch
              color={props.form?.status ? "success" : "error"}
              checked={props.form?.status}
              onChange={handleChangeSwitch}
              sx={{
                "& .MuiSwitch-switchBase": {
                  transitionDuration: "300ms",
                  "&.Mui-checked": {
                    transform: "translateX(26px)",
                    color: "#fff",
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#2EA689",
                    },
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#E74C3C",
                  },
                },
              }}
            />
            <Tooltip title={'Este modelo de formulário possui fluxo'} arrow>
              <Badge
                badgeContent={<AccountTreeIcon fontSize='small' />}
                color="primary"
                invisible={!props.form.fluxo}
                sx={{
                  "& .MuiBadge-badge": {
                    marginTop: 2,
                    height: 20,
                    width: 20,
                    padding: 2,
                    borderRadius: '50%'
                  }
                }}
              ></Badge>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>
    </>
  );
}
